import { useState, useEffect, useCallback } from "react";

// ── Constants ─────────────────────────────────────────────
const HOURLY_RATE = 14;
const MONTH_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTH_DE = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

// ── Translations ──────────────────────────────────────────
const T = {
  en: {
    title:"Schlosskeller", subtitle:"Theke", enterPin:"Enter your PIN",
    pinPlaceholder:"Your PIN", loginBtn:"Login", wrongPin:"Wrong PIN",
    arrive:"Arrived", finish:"Finished", currentlyWorking:"Working since",
    thisMonth:"This month", hours:"hours", earned:"earned", history:"History",
    admin:"Admin", addWorker:"Add worker", removeWorker:"Remove",
    workerName:"Full name", add:"Add", monthlyReport:"Monthly report",
    exportPDF:"Export PDF", name:"Name", totalHours:"Total hours",
    totalEarned:"Total earned", noSessions:"No sessions yet.",
    workerReport:"My report", date:"Date", start:"Start", end:"End", lang:"DE",
    alreadyClockedIn:"Already clocked in!", notClockedIn:"Not clocked in.",
    sessionSaved:"Session saved!", roundedNote:"Hours rounded to nearest half hour.",
    resetPin:"Reset PIN", newPin:"New PIN (6+ digits)", setPin:"Set PIN",
    pinReset:"PIN reset!", editSession:"Edit", editSessions:"Edit sessions",
    saveEdit:"Save", cancelEdit:"Cancel", deleteSession:"Delete",
    confirmDelete:"Delete this session?", pinTooShort:"PIN must be at least 6 digits.",
    workers:"Workers", noPin:"No PIN set yet — ask admin.",
    workerAdded:"Worker added!", workerExists:"Name already exists.",
    loading:"Loading...", error:"Something went wrong. Please try again.",
    logout:"Logout", selectWorker:"Select worker",
    addSession:"Add session", addSessionTitle:"Add session manually",
    adminLogin:"Admin login", adminLogout:"Admin logout",
    notAuthorized:"You are not authorized as admin.",
    allWorkers:"All workers", signature:"Signature",
    confirmText:"I confirm that the hours listed above are correct.",
    currentMonthOnly:"You can only edit, delete or add sessions from the current month.",
    sessionExpired:"Your session has expired. Please log out and log in again.",
    formatHint:"Date as DD.MM.YYYY (e.g. 03.09.2026), time as HH:MM (e.g. 18:30) — dots for the date, a colon for the time.",
    refresh:"Refresh",
  },
  de: {
    title:"Schlosskeller", subtitle:"Theke", enterPin:"PIN eingeben",
    pinPlaceholder:"Deine PIN", loginBtn:"Anmelden", wrongPin:"Falsche PIN",
    arrive:"Angekommen", finish:"Fertig", currentlyWorking:"Arbeitet seit",
    thisMonth:"Diesen Monat", hours:"Stunden", earned:"Verdient", history:"Verlauf",
    admin:"Admin", addWorker:"Mitarbeiter hinzufügen", removeWorker:"Entfernen",
    workerName:"Vollständiger Name", add:"Hinzufügen", monthlyReport:"Monatsbericht",
    exportPDF:"PDF exportieren", name:"Name", totalHours:"Gesamtstunden",
    totalEarned:"Gesamt verdient", noSessions:"Noch keine Einträge.",
    workerReport:"Mein Bericht", date:"Datum", start:"Beginn", end:"Ende", lang:"EN",
    alreadyClockedIn:"Bereits eingestempelt!", notClockedIn:"Nicht eingestempelt.",
    sessionSaved:"Gespeichert!", roundedNote:"Stunden werden auf die nächste halbe Stunde gerundet.",
    resetPin:"PIN zurücksetzen", newPin:"Neue PIN (mind. 6 Stellen)", setPin:"PIN setzen",
    pinReset:"PIN zurückgesetzt!", editSession:"Bearbeiten", editSessions:"Einträge bearbeiten",
    saveEdit:"Speichern", cancelEdit:"Abbrechen", deleteSession:"Löschen",
    confirmDelete:"Diesen Eintrag löschen?", pinTooShort:"PIN muss mindestens 6 Stellen haben.",
    workers:"Mitarbeiter", noPin:"Noch keine PIN — Admin kontaktieren.",
    workerAdded:"Mitarbeiter hinzugefügt!", workerExists:"Name existiert bereits.",
    loading:"Laden...", error:"Etwas ist schiefgelaufen. Bitte erneut versuchen.",
    logout:"Abmelden", selectWorker:"Mitarbeiter wählen",
    addSession:"Eintrag hinzufügen", addSessionTitle:"Eintrag manuell hinzufügen",
    adminLogin:"Admin-Anmeldung", adminLogout:"Admin abmelden",
    notAuthorized:"Sie sind nicht als Admin autorisiert.",
    allWorkers:"Alle Mitarbeiter", signature:"Unterschrift",
    confirmText:"Ich bestätige, dass die oben aufgeführten Stunden korrekt sind.",
    currentMonthOnly:"Du kannst nur Einträge aus dem laufenden Monat bearbeiten, löschen oder hinzufügen.",
    sessionExpired:"Deine Sitzung ist abgelaufen. Bitte melde dich ab und wieder an.",
    formatHint:"Datum im Format TT.MM.JJJJ (z. B. 03.09.2026), Uhrzeit im Format HH:MM (z. B. 18:30) — Punkte beim Datum, Doppelpunkt bei der Uhrzeit.",
    refresh:"Aktualisieren",
  }
};

// ── Styles ────────────────────────────────────────────────
const S = {
  app:      { minHeight:"100vh", background:"var(--color-background-tertiary)", fontFamily:"var(--font-sans)", paddingBottom:"2rem" },
  header:   { background:"#7a1c2e", color:"#fff", padding:"14px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" },
  hLeft:    { display:"flex", alignItems:"center", gap:"12px" },
  hTitle:   { margin:0, fontSize:"18px", fontWeight:500, color:"#fff" },
  hSub:     { margin:0, fontSize:"12px", color:"#f5c6c6" },
  hRight:   { display:"flex", alignItems:"center", gap:"8px" },
  langBtn:  { background:"rgba(255,255,255,0.2)", border:"none", color:"#fff", padding:"4px 10px", borderRadius:"6px", cursor:"pointer", fontSize:"13px" },
  logoutBtn:{ background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", color:"#fff", padding:"4px 10px", borderRadius:"6px", cursor:"pointer", fontSize:"13px" },
  workerNameStyle:{ margin:0, fontSize:"13px", color:"#f5c6c6" },
  wrap:     { maxWidth:"480px", margin:"0 auto", padding:"1rem" },
  card:     { background:"var(--color-background-primary)", borderRadius:"12px", border:"0.5px solid var(--color-border-tertiary)", padding:"1.25rem", marginBottom:"1rem" },
  sel:      { width:"100%", padding:"10px 12px", fontSize:"15px", borderRadius:"8px", border:"0.5px solid var(--color-border-secondary)", background:"var(--color-background-primary)", color:"var(--color-text-primary)", marginBottom:"1rem", boxSizing:"border-box" },
  btnRow:   { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" },
  btnA:     { padding:"14px", fontSize:"15px", fontWeight:500, borderRadius:"8px", border:"none", background:"#7a1c2e", color:"#fff", cursor:"pointer" },
  btnF:     { padding:"14px", fontSize:"15px", fontWeight:500, borderRadius:"8px", border:"none", background:"#2d6a4f", color:"#fff", cursor:"pointer" },
  dis:      { opacity:0.4, cursor:"not-allowed" },
  mRow:     { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px", marginBottom:"1rem" },
  metric:   { background:"var(--color-background-secondary)", borderRadius:"8px", padding:"0.75rem 1rem", textAlign:"center" },
  mVal:     { fontSize:"22px", fontWeight:500, color:"var(--color-text-primary)", margin:0 },
  mLbl:     { fontSize:"12px", color:"var(--color-text-secondary)", margin:"2px 0 0" },
  sec:      { fontSize:"13px", fontWeight:500, color:"var(--color-text-secondary)", textTransform:"uppercase", letterSpacing:"0.05em", margin:"0 0 10px" },
  badge:    { background:"#fef3c7", color:"#92400e", borderRadius:"6px", padding:"8px 12px", fontSize:"13px", marginBottom:"1rem", textAlign:"center" },
  toast:    { position:"fixed", bottom:"24px", left:"50%", transform:"translateX(-50%)", background:"#1f2937", color:"#fff", padding:"10px 20px", borderRadius:"8px", fontSize:"14px", zIndex:999, whiteSpace:"nowrap" },
  navRow:   { display:"flex", gap:"8px", marginBottom:"1rem", flexWrap:"wrap" },
  navBtn:   { padding:"7px 14px", fontSize:"13px", borderRadius:"6px", border:"0.5px solid var(--color-border-secondary)", background:"var(--color-background-primary)", color:"var(--color-text-primary)", cursor:"pointer" },
  navAct:   { background:"#7a1c2e", color:"#fff", border:"none" },
  inp:      { width:"100%", padding:"9px 12px", fontSize:"14px", borderRadius:"8px", border:"0.5px solid var(--color-border-secondary)", background:"var(--color-background-primary)", color:"var(--color-text-primary)", boxSizing:"border-box" },
  table:    { width:"100%", borderCollapse:"collapse", fontSize:"13px" },
  th:       { padding:"8px 10px", textAlign:"left", background:"#f5ede8", color:"#7a1c2e", fontWeight:500, borderBottom:"1px solid #e8d5d0" },
  td:       { padding:"8px 10px", borderBottom:"0.5px solid var(--color-border-tertiary)", color:"var(--color-text-primary)" },
  btn:      { background:"#7a1c2e", color:"#fff", border:"none", padding:"9px 18px", borderRadius:"8px", cursor:"pointer", fontSize:"14px" },
  btnSm:    { border:"0.5px solid var(--color-border-secondary)", borderRadius:"6px", padding:"3px 10px", cursor:"pointer", fontSize:"12px", background:"none" },
  err:      { color:"var(--color-text-danger)", fontSize:"13px", margin:"0 0 8px" },
  pinWrap:  { display:"flex", flexDirection:"column", gap:"10px" },
  dimText:  { fontSize:"11px", color:"var(--color-text-tertiary)", margin:0 },
  pinScreen:{ minHeight:"100vh", background:"var(--color-background-tertiary)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"2rem" },
  pinCard:  { background:"var(--color-background-primary)", borderRadius:"16px", padding:"2rem", width:"100%", maxWidth:"360px", display:"flex", flexDirection:"column", gap:"16px", boxShadow:"0 4px 24px rgba(0,0,0,0.08)" },
  pinLogo:  { display:"flex", flexDirection:"column", alignItems:"center", gap:"8px", marginBottom:"8px" },
  msBtn:    { display:"flex", alignItems:"center", justifyContent:"center", gap:"10px", background:"#0078d4", color:"#fff", border:"none", padding:"10px 18px", borderRadius:"8px", cursor:"pointer", fontSize:"14px", fontWeight:500 },
};

// ── Helpers ───────────────────────────────────────────────
// Where a worker's remembered login lives in this browser. Safe to persist
// (each worker uses their own phone) — it just restores the PIN-login UI
// state; every actual edit/delete/add is still re-checked by the server
// against the token's own expiry, so a stale remembered login can't do
// anything a fresh PIN login couldn't also immediately do.
const WORKER_STORAGE_KEY = "sk_worker_session";
const pad = n => String(n).padStart(2,"0");
const monthKey = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}`;
const monthLabel = (key, lang) => {
  const [y,m] = key.split("-");
  return `${(lang==="en"?MONTH_EN:MONTH_DE)[parseInt(m)-1]} ${y}`;
};
const api = async (path, method="GET", body=null, workerToken=null) => {
  const opts = { method, headers:{"Content-Type":"application/json"} };
  if (workerToken) opts.headers["x-worker-token"] = workerToken;
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`/api/${path}`, opts);
  if (!res.ok) {
    // e.message stays exactly the status code, as before, so existing
    // e.message==="409" style checks keep working unchanged; e.detail
    // carries the server's specific error text when there is one.
    const err = new Error(`${res.status}`);
    try { const body = await res.json(); err.detail = body?.error || ""; err.code = body?.code || ""; } catch {}
    throw err;
  }
  return res.json();
};

// ── Reusable components ───────────────────────────────────
const SessionTable = ({ sessions, t }) => (
  <table style={S.table}>
    <thead><tr>
      <th style={S.th}>{t.date}</th><th style={S.th}>{t.start}</th>
      <th style={S.th}>{t.end}</th><th style={S.th}>{t.hours}</th><th style={S.th}>€</th>
    </tr></thead>
    <tbody>
      {sessions.map((s,i) => (
        <tr key={i}>
          <td style={S.td}>{s.date}</td><td style={S.td}>{s.start}</td>
          <td style={S.td}>{s.end}</td><td style={S.td}>{s.hours}</td><td style={S.td}>{s.earned}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

// Date/start/end input trio + the format hint underneath, in one place —
// so the hint text (and the fields themselves) are identical everywhere:
// the admin's "add session", the worker's "add session", and any inline
// edit row. onChange receives a functional update, e.g. setNewSession.
const SessionFields = ({ value, onChange, t }) => (
  <>
    <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"6px", marginBottom:"4px"}}>
      <input style={S.inp} value={value.date}  onChange={e => onChange(p=>({...p, date:e.target.value}))}  placeholder="DD.MM.YYYY" />
      <input style={S.inp} value={value.start} onChange={e => onChange(p=>({...p, start:e.target.value}))} placeholder="HH:MM" />
      <input style={S.inp} value={value.end}   onChange={e => onChange(p=>({...p, end:e.target.value}))}   placeholder="HH:MM" />
    </div>
    <p style={{...S.dimText, marginBottom:"8px"}}>{t.formatHint}</p>
  </>
);

// Inline edit/delete rows for a set of sessions — used both by the admin's
// "Edit sessions" panel and by a worker editing their own current month.
const EditableSessionList = ({ sessions, t, editTarget, editData, onEditStart, onEditChange, onEditSave, onEditCancel, onDelete, busy }) => (
  <div>
    {sessions.map(s => (
      <div key={s.id}>
        {editTarget?.id===s.id ? (
          <div style={{padding:"10px 0",borderBottom:"0.5px solid var(--color-border-tertiary)"}}>
            <SessionFields value={editData} onChange={onEditChange} t={t} />
            <div style={{display:"flex",gap:"8px"}}>
              <button style={S.btn} onClick={onEditSave} disabled={busy}>{t.saveEdit}</button>
              <button style={{...S.btn,background:"var(--color-background-secondary)",color:"var(--color-text-primary)"}}
                onClick={onEditCancel}>{t.cancelEdit}</button>
            </div>
          </div>
        ) : (
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"0.5px solid var(--color-border-tertiary)",fontSize:"13px"}}>
            <span style={{color:"var(--color-text-primary)"}}>{s.date} {s.start}–{s.end} ({s.hours}h)</span>
            <div style={{display:"flex",gap:"6px"}}>
              <button style={{...S.btnSm,color:"#2d6a4f"}} onClick={() => onEditStart(s)}>{t.editSession}</button>
              <button style={{...S.btnSm,color:"var(--color-text-danger)"}} onClick={() => onDelete(s.id)}>{t.deleteSession}</button>
            </div>
          </div>
        )}
      </div>
    ))}
  </div>
);

const Metrics = ({ hours, earned, t }) => (
  <div style={S.mRow}>
    <div style={S.metric}><p style={S.mVal}>{hours}</p><p style={S.mLbl}>{t.hours}</p></div>
    <div style={S.metric}><p style={S.mVal}>€{earned}</p><p style={S.mLbl}>{t.earned}</p></div>
  </div>
);

// ── PIN Login Screen ──────────────────────────────────────
const PinLoginScreen = ({ onLogin, onAdminLogin, lang, setLang, t }) => {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const handleLogin = async () => {
    if (pin.length < 6) { setErr(t.pinTooShort); return; }
    setBusy(true);
    try {
      const result = await api("loginByPin", "POST", { pin });
      onLogin(result.name, result.token);
    } catch(e) {
      if (e.message === "401") setErr(t.wrongPin);
      else if (e.message === "429") setErr("Too many attempts. Please wait.");
      else setErr(t.error);
      setPin("");
    } finally { setBusy(false); }
  };

  return (
    <div style={S.pinScreen}>
      <div style={S.pinCard}>
        <div style={S.pinLogo}>
          <img src="/sk_logo.png" alt="SK Logo" style={{width:"80px", height:"80px", borderRadius:"8px"}} />
          <p style={{margin:0, fontSize:"18px", fontWeight:500, color:"#7a1c2e"}}>{t.title}</p>
          <p style={{margin:0, fontSize:"13px", color:"var(--color-text-secondary)"}}>{t.subtitle}</p>
        </div>
        <input
          type="password" inputMode="numeric" style={S.inp}
          placeholder={t.pinPlaceholder} value={pin}
          onChange={e => { setPin(e.target.value); setErr(""); }}
          onKeyDown={e => e.key==="Enter" && handleLogin()}
          maxLength={10} autoComplete="off" autoFocus
        />
        {err && <p style={S.err}>{err}</p>}
        <button style={{...S.btn, ...(busy?S.dis:{})}} disabled={busy} onClick={handleLogin}>
          {t.loginBtn}
        </button>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
          <button style={S.langBtn} onClick={() => setLang(l => l==="en"?"de":"en")}>
            {t.lang}
          </button>
          <button
            style={{background:"none", border:"none", color:"var(--color-text-tertiary)", fontSize:"12px", cursor:"pointer", padding:"4px"}}
            onClick={onAdminLogin}>
            {t.admin}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main App ──────────────────────────────────────────────
export default function App() {
  const [lang, setLang] = useState("en");
  const t = T[lang];

  // Auth state
  const [workerName,    setWorkerName]    = useState("");
  const [workerToken,   setWorkerToken]   = useState("");
  const [adminAuth,     setAdminAuth]     = useState(false);
  const [adminUser,     setAdminUser]     = useState(null);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  // Data state
  const [workers,        setWorkers]        = useState([]);
  const [sessions,       setSessions]       = useState([]);
  const [clockedIn,      setClockedIn]      = useState(null);
  const [monthlyReport,  setMonthlyReport]  = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(true);

  // UI state
  const [view,         setView]         = useState("main");
  const [reportMonth,  setReportMonth]  = useState(monthKey(new Date()));
  const [newWorkerName,setNewWorkerName]= useState("");
  const [resetTarget,  setResetTarget]  = useState("");
  const [newPinVal,    setNewPinVal]    = useState("");
  const [newPinErr,    setNewPinErr]    = useState("");
  const [editTarget,   setEditTarget]   = useState(null);
  const [editData,     setEditData]     = useState({});
  const [newSession,   setNewSession]   = useState({ date:"", start:"", end:"" });
  // Worker's own edit/add state (kept separate from the admin panel's
  // above, since in principle both could be visible at once)
  const [wEditTarget,  setWEditTarget]  = useState(null);
  const [wEditData,    setWEditData]    = useState({});
  const [wNewSession,  setWNewSession]  = useState({ date:"", start:"", end:"" });
  const [busy,         setBusy]         = useState(false);
  const [toast,        setToast]        = useState("");
  const [selWorker,    setSelWorker]    = useState("");

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(""), 2800); };

  // ── Bootstrap: restore a remembered worker login (this device only),
  // and silently check for an active admin (Microsoft) session — runs on
  // every page load, not just right after the Microsoft login redirect,
  // so refreshing the browser (or reopening the app) never forces a
  // re-login that wasn't actually necessary.
  useEffect(() => {
    const bootstrap = async () => {
      try {
        const saved = localStorage.getItem(WORKER_STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed?.name) {
            setWorkerName(parsed.name);
            setWorkerToken(parsed.token || "");
          }
        }
      } catch { /* corrupt or blocked storage — just skip restoring */ }

      try {
        const res = await fetch("/.auth/me");
        const data = await res.json();
        if (data.clientPrincipal) {
          setAdminUser(data.clientPrincipal);
          setAdminAuth(true);
          setView("admin");
        }
      } catch { /* not logged in as admin */ }

      sessionStorage.removeItem("adminLoginPending");
      setCheckingAdmin(false);
    };
    bootstrap();
  }, []);

  // Whenever we have a worker identity — freshly logged in, or restored
  // from a remembered login above — (re)load their clock-in status and
  // sessions. Centralizing this here (instead of duplicating it at login)
  // means a restored session and a fresh login behave identically.
  useEffect(() => {
    if (!workerName) return;
    let cancelled = false;
    (async () => {
      try {
        const [ci, data] = await Promise.all([
          api(`getClockedIn?name=${encodeURIComponent(workerName)}`),
          api(`getSessions?name=${encodeURIComponent(workerName)}`)
        ]);
        if (!cancelled) { setClockedIn(ci.arrivedAt || null); setSessions(data); }
      } catch { if (!cancelled) showToast(t.error); }
    })();
    return () => { cancelled = true; };
  }, [workerName]);

  // ── After worker PIN login ────────────────────────────────
  const handleWorkerLogin = (name, token) => {
    setWorkerName(name);
    setWorkerToken(token || "");
    setView("main");
    try {
      localStorage.setItem(WORKER_STORAGE_KEY, JSON.stringify({ name, token: token || "" }));
    } catch { /* storage unavailable — login still works, just won't be remembered */ }
  };

  const handleWorkerLogout = () => {
    setWorkerName(""); setWorkerToken(""); setClockedIn(null); setSessions([]);
    setView("main");
    try { localStorage.removeItem(WORKER_STORAGE_KEY); } catch {}
  };

  // ── Admin login via Microsoft ─────────────────────────────
  const handleAdminLogin = () => {
    sessionStorage.setItem("adminLoginPending", "1");
    window.location.href = "/.auth/login/aad?post_login_redirect_uri=/";
  };

  const handleAdminLogout = () => {
    setAdminAuth(false); setAdminUser(null); setView("main");
    window.location.href = "/.auth/logout?post_logout_redirect_uri=/";
  };

  // ── Data loaders ─────────────────────────────────────────
  useEffect(() => {
    api("getWorkers")
      .then(data => setWorkers(data.map(w => w.name)))
      .catch(() => {})
      .finally(() => setLoadingWorkers(false));
  }, []);

  const loadMonthlyReport = useCallback((month) => {
    api(`getMonthlyReport?month=${month}`)
      .then(setMonthlyReport)
      .catch(() => showToast(t.error));
  }, [t.error]);

  useEffect(() => {
    if (adminAuth) loadMonthlyReport(reportMonth);
  }, [adminAuth, reportMonth, loadMonthlyReport]);

  // ── Derived data ──────────────────────────────────────────
  const allMonths = () => {
    const months = [];
    const start = new Date(2026, 7, 1); // August 2026
    const now = new Date();
    let current = new Date(start.getFullYear(), start.getMonth(), 1);
    while (current <= now) {
      months.push(monthKey(current));
      current.setMonth(current.getMonth() + 1);
    }
    return months.reverse();
  };
  const monthSessions = month => sessions.filter(s => s.month === month);
  const monthSummary  = month => {
    const list = monthSessions(month);
    const h = list.reduce((s,x) => s+x.hours, 0);
    return { hours:h, earned:h*HOURLY_RATE, sessions:list };
  };

  // ── Handlers ─────────────────────────────────────────────
  const switchView = k => setView(k);

  const handleArrive = async () => {
    setBusy(true);
    try {
      await api("clockIn", "POST", { name:workerName });
      const ci = await api(`getClockedIn?name=${encodeURIComponent(workerName)}`);
      setClockedIn(ci.arrivedAt);
      showToast(`${workerName}: ${t.arrive} ✓`);
    } catch(e) {
      showToast(e.message==="409" ? t.alreadyClockedIn : t.error);
    } finally { setBusy(false); }
  };

  const handleFinish = async () => {
    setBusy(true);
    try {
      const result = await api("clockOut", "POST", { name:workerName });
      setClockedIn(null);
      showToast(`${t.sessionSaved} ${result.hours}h = €${result.earned}`);
      const data = await api(`getSessions?name=${encodeURIComponent(workerName)}`);
      setSessions(data);
    } catch(e) {
      showToast(e.message==="404" ? t.notClockedIn : t.error);
    } finally { setBusy(false); }
  };

  const handleResetPin = async () => {
    if (newPinVal.length < 6) { setNewPinErr(t.pinTooShort); return; }
    setBusy(true);
    try {
      await api("setPin", "POST", { name:resetTarget, pin:newPinVal });
      setNewPinVal(""); setResetTarget(""); setNewPinErr("");
      showToast(`${resetTarget}: ${t.pinReset}`);
    } catch { setNewPinErr(t.error); }
    finally { setBusy(false); }
  };

  const handleAddWorker = async () => {
    const n = newWorkerName.trim();
    if (!n) return;
    if (workers.includes(n)) { showToast(t.workerExists); return; }
    setBusy(true);
    try {
      await api("saveWorker", "POST", { name:n });
      setWorkers(p => [...p, n]); setNewWorkerName("");
      showToast(t.workerAdded);
    } catch { showToast(t.error); }
    finally { setBusy(false); }
  };

  const handleRemoveWorker = async w => {
    if (!window.confirm(`Remove ${w}?`)) return;
    setBusy(true);
    try {
      await api("deleteWorker", "DELETE", { name:w });
      setWorkers(p => p.filter(x => x!==w));
    } catch { showToast(t.error); }
    finally { setBusy(false); }
  };

  const handleAddSession = async () => {
    setBusy(true);
    try {
      await api("addSession", "POST", {
        name: selWorker, date: newSession.date,
        start: newSession.start, end: newSession.end
      });
      showToast(t.sessionSaved);
      setNewSession({ date:"", start:"", end:"" });
      const data = await api(`getSessions?name=${encodeURIComponent(selWorker)}&month=${reportMonth}`);
      setSessions(data);
      loadMonthlyReport(reportMonth); // keep the report totals in sync, not just this list
    } catch { showToast(t.error); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const result = await api("updateSession", "POST", {
        name:selWorker, id:editTarget.id,
        date:editData.date, start:editData.start, end:editData.end
      });
      setSessions(p => p.map(s => s.id===editTarget.id
        ? {...s,...editData, hours:result.hours, earned:result.earned} : s));
      setEditTarget(null);
      showToast(t.sessionSaved);
      loadMonthlyReport(reportMonth); // keep the report totals in sync, not just this list
    } catch { showToast(t.error); }
    finally { setBusy(false); }
  };

  const deleteSession = async id => {
    if (!window.confirm(t.confirmDelete)) return;
    setBusy(true);
    try {
      await api("deleteSession", "DELETE", { name:selWorker, id });
      setSessions(p => p.filter(s => s.id!==id));
      loadMonthlyReport(reportMonth); // keep the report totals in sync, not just this list
    } catch { showToast(t.error); }
    finally { setBusy(false); }
  };

  // ── Worker self-service: own sessions, current month only ──
  const refreshMySessions = async () => {
    const data = await api(`getSessions?name=${encodeURIComponent(workerName)}`);
    setSessions(data);
  };

  // Turns a failed worker self-edit into the right message: a precise
  // "please log in again" when the token is what's wrong (almost always
  // just expired), the current-month rule when that's what blocked it,
  // and a generic fallback otherwise.
  const workerErrorMessage = e =>
    e.code === "session_expired" ? t.sessionExpired :
    e.code === "month_locked"    ? t.currentMonthOnly :
    (e.detail || t.error);

  const handleWorkerAddSession = async () => {
    setBusy(true);
    try {
      await api("addSession", "POST", {
        name: workerName, date: wNewSession.date,
        start: wNewSession.start, end: wNewSession.end
      }, workerToken);
      showToast(t.sessionSaved);
      setWNewSession({ date:"", start:"", end:"" });
      await refreshMySessions();
    } catch(e) { showToast(workerErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const saveWorkerEdit = async () => {
    setBusy(true);
    try {
      const result = await api("updateSession", "POST", {
        name:workerName, id:wEditTarget.id,
        date:wEditData.date, start:wEditData.start, end:wEditData.end
      }, workerToken);
      setSessions(p => p.map(s => s.id===wEditTarget.id
        ? {...s,...wEditData, hours:result.hours, earned:result.earned} : s));
      setWEditTarget(null);
      showToast(t.sessionSaved);
    } catch(e) { showToast(workerErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const deleteWorkerSession = async id => {
    if (!window.confirm(t.confirmDelete)) return;
    setBusy(true);
    try {
      await api("deleteSession", "DELETE", { name:workerName, id }, workerToken);
      setSessions(p => p.filter(s => s.id!==id));
    } catch(e) { showToast(workerErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const exportPDF = workerFilter => {
    const data = workerFilter
      ? monthlyReport.filter(w => w.name===workerFilter)
      : monthlyReport;
    const rows = data.map(w =>
      `<tr><td>${w.name}</td><td>${w.hours}</td><td>€${w.earned.toFixed(2)}</td></tr>`
    ).join("");
    const signatureBlock = workerFilter ? `
    <p style="margin-top:2.5rem;font-size:13px;color:#333;">${t.confirmText}</p>
    <div style="display:flex;gap:3rem;margin-top:3.5rem;">
      <div style="flex:1;border-top:1px solid #333;padding-top:6px;font-size:12px;color:#555;">${t.date}</div>
      <div style="flex:1;border-top:1px solid #333;padding-top:6px;font-size:12px;color:#555;">${t.signature}</div>
    </div>` : "";
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Report</title>
    <style>body{font-family:sans-serif;padding:2rem;}h1{color:#7a1c2e;}
    table{width:100%;border-collapse:collapse;}
    th,td{border:1px solid #ccc;padding:8px 12px;text-align:left;}
    th{background:#f5ede8;color:#7a1c2e;}</style></head><body>
    <h1>Kulturverein Windecker Schlosskeller e.V.</h1>
    <p><strong>${lang==="en"?"Monthly Report":"Monatsbericht"}:</strong> ${monthLabel(reportMonth,lang)}</p>
    <table><thead><tr><th>${t.name}</th><th>${t.totalHours}</th><th>${t.totalEarned}</th></tr></thead>
    <tbody>${rows}</tbody></table>
    ${signatureBlock}
    </body></html>`;
    const w2 = window.open("","_blank");
    w2.document.write(html); w2.document.close(); w2.print();
  };

  const navItems = [
    {k:"main",         label:"🏠"},
    {k:"history",      label:t.history},
    {k:"workerReport", label:t.workerReport},
  ];

  // ── Show PIN login screen if worker not yet identified ────
  if (!workerName && !adminAuth) {
    if (checkingAdmin) return <div style={{...S.pinScreen}}><p>Loading...</p></div>;
    return (
      <PinLoginScreen
        onLogin={handleWorkerLogin}
        onAdminLogin={handleAdminLogin}
        lang={lang} setLang={setLang} t={t}
      />
    );
  }

  // ── Main app shell ────────────────────────────────────────
  return (
    <div style={S.app}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.hLeft}>
          <img src="/sk_logo.png" alt="SK Logo" style={{width:"48px", height:"48px", borderRadius:"8px"}} />
          <div><p style={S.hTitle}>{t.title}</p><p style={S.hSub}>{t.subtitle}</p></div>
        </div>
        <div style={S.hRight}>
          {workerName && <p style={S.workerNameStyle}>{workerName}</p>}
          {adminAuth && <p style={S.workerNameStyle}>{adminUser?.userDetails || "Admin"}</p>}
          <button style={S.langBtn} onClick={() => setLang(l => l==="en"?"de":"en")}>{t.lang}</button>
          {workerName && <button style={S.logoutBtn} onClick={handleWorkerLogout}>{t.logout}</button>}
          {adminAuth && <button style={S.logoutBtn} onClick={handleAdminLogout}>{t.adminLogout}</button>}
        </div>
      </div>

      <div style={S.wrap}>
        {/* Nav — only for workers */}
        {workerName && (
          <div style={S.navRow}>
            {navItems.map(({k,label}) => (
              <button key={k} style={{...S.navBtn,...(view===k?S.navAct:{})}}
                onClick={() => switchView(k)}>{label}</button>
            ))}
          </div>
        )}

        {/* ── Home ── */}
        {view==="main" && workerName && (
          <>
            <div style={S.card}>
              <p style={S.sec}>{workerName}</p>
              {clockedIn && (
                <div style={S.badge}>
                  ⏱ {t.currentlyWorking} {new Date(clockedIn).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}
                </div>
              )}
              <div style={S.btnRow}>
                <button style={{...S.btnA,...((clockedIn||busy)?S.dis:{})}} disabled={!!clockedIn||busy} onClick={handleArrive}>{t.arrive}</button>
                <button style={{...S.btnF,...((!clockedIn||busy)?S.dis:{})}} disabled={!clockedIn||busy} onClick={handleFinish}>{t.finish}</button>
              </div>
            </div>
            <div style={S.card}>
              <p style={S.sec}>{t.thisMonth}</p>
              <Metrics hours={monthSummary(reportMonth).hours} earned={monthSummary(reportMonth).earned} t={t} />
              <p style={S.dimText}>{t.roundedNote}</p>
            </div>
          </>
        )}

        {/* ── History ── */}
        {view==="history" && workerName && (
          <div style={S.card}>
            <p style={S.sec}>{t.history} — {workerName}</p>
            {allMonths().map(m => {
              const {hours, earned, sessions:ss} = monthSummary(m);
              const isCurrent = m === monthKey(new Date());
              return (
                <details key={m} style={{marginBottom:"10px"}} open={isCurrent}>
                  <summary style={{cursor:"pointer",fontWeight:500,fontSize:"14px",padding:"6px 0",color:"var(--color-text-primary)"}}>
                    {monthLabel(m,lang)} — {hours}h / €{earned}
                  </summary>
                  {ss.length===0
                    ? <p style={{fontSize:"13px",color:"var(--color-text-secondary)",margin:"8px 0"}}>{t.noSessions}</p>
                    : (isCurrent
                        ? <EditableSessionList
                            sessions={ss} t={t}
                            editTarget={wEditTarget} editData={wEditData}
                            onEditStart={s => { setWEditTarget(s); setWEditData({date:s.date,start:s.start,end:s.end}); }}
                            onEditChange={setWEditData}
                            onEditSave={saveWorkerEdit}
                            onEditCancel={() => setWEditTarget(null)}
                            onDelete={deleteWorkerSession}
                            busy={busy}
                          />
                        : <SessionTable sessions={ss} t={t} />)}
                  {isCurrent && (
                    <div style={{marginTop:"10px", padding:"10px", background:"var(--color-background-secondary)", borderRadius:"8px"}}>
                      <p style={{...S.sec, marginBottom:"8px"}}>{t.addSessionTitle}</p>
                      <SessionFields value={wNewSession} onChange={setWNewSession} t={t} />
                      <button style={S.btn} onClick={handleWorkerAddSession} disabled={busy}>{t.addSession}</button>
                      <p style={S.dimText}>{t.currentMonthOnly}</p>
                    </div>
                  )}
                </details>
              );
            })}
          </div>
        )}

        {/* ── My Report ── */}
        {view==="workerReport" && workerName && (
          <div style={S.card}>
            <p style={S.sec}>{t.workerReport} — {workerName}</p>
            <select style={S.sel} value={reportMonth} onChange={e => setReportMonth(e.target.value)}>
              {allMonths().map(m => <option key={m} value={m}>{monthLabel(m,lang)}</option>)}
            </select>
            <Metrics hours={monthSummary(reportMonth).hours} earned={monthSummary(reportMonth).earned} t={t} />
            <SessionTable sessions={monthSummary(reportMonth).sessions} t={t} />
            <button style={{...S.btn,marginTop:"12px"}} onClick={() => exportPDF(workerName)}>{t.exportPDF}</button>
          </div>
        )}

        {/* ── Admin Panel ── */}
        {adminAuth && (<>
          {/* Workers */}
          <div style={S.card}>
            <p style={S.sec}>{t.workers}</p>
            <div style={{display:"flex",gap:"8px",marginBottom:"1rem"}}>
              <input style={{...S.inp,flex:1}} placeholder={t.workerName} value={newWorkerName}
                onChange={e => setNewWorkerName(e.target.value)}
                onKeyDown={e => e.key==="Enter"&&handleAddWorker()} />
              <button style={S.btn} onClick={handleAddWorker} disabled={busy}>{t.add}</button>
            </div>
            {workers.map(w => (
              <div key={w} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"0.5px solid var(--color-border-tertiary)"}}>
                <span style={{fontSize:"14px",color:"var(--color-text-primary)"}}>{w}</span>
                <div style={{display:"flex",gap:"6px"}}>
                  <button style={{...S.btnSm,color:"#2d6a4f"}} onClick={() => { setResetTarget(w); setNewPinVal(""); setNewPinErr(""); }}>{t.resetPin}</button>
                  <button style={{...S.btnSm,color:"var(--color-text-danger)"}} onClick={() => handleRemoveWorker(w)}>{t.removeWorker}</button>
                </div>
              </div>
            ))}
          </div>

          {/* PIN Reset */}
          {resetTarget && (
            <div style={S.card}>
              <p style={S.sec}>{t.resetPin}: {resetTarget}</p>
              <div style={S.pinWrap}>
                <input type="password" inputMode="numeric" style={S.inp} placeholder={t.newPin}
                  value={newPinVal} onChange={e => setNewPinVal(e.target.value)}
                  maxLength={10} autoComplete="off" onKeyDown={e => e.key==="Enter"&&handleResetPin()} />
                {newPinErr && <p style={S.err}>{newPinErr}</p>}
                <div style={{display:"flex",gap:"8px"}}>
                  <button style={S.btn} onClick={handleResetPin} disabled={busy}>{t.setPin}</button>
                  <button style={{...S.btn,background:"var(--color-background-secondary)",color:"var(--color-text-primary)"}}
                    onClick={() => setResetTarget("")}>{t.cancelEdit}</button>
                </div>
              </div>
            </div>
          )}

          {/* Monthly Report */}
          <div style={S.card}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"10px"}}>
              <p style={{...S.sec, margin:0}}>{t.monthlyReport}</p>
              <button style={S.btnSm} onClick={() => loadMonthlyReport(reportMonth)} disabled={busy}>↻ {t.refresh}</button>
            </div>
            <select style={S.sel} value={reportMonth} onChange={e => { setReportMonth(e.target.value); loadMonthlyReport(e.target.value); }}>
              {allMonths().map(m => <option key={m} value={m}>{monthLabel(m,lang)}</option>)}
            </select>
            <table style={S.table}><thead><tr>
              <th style={S.th}>{t.name}</th><th style={S.th}>{t.totalHours}</th><th style={S.th}>{t.totalEarned}</th><th style={S.th}></th>
            </tr></thead><tbody>
              {monthlyReport.map(w => (
                <tr key={w.name}>
                  <td style={S.td}>{w.name}</td>
                  <td style={S.td}>{w.hours}</td>
                  <td style={S.td}>€{w.earned.toFixed(2)}</td>
                  <td style={S.td}>
                    <button style={{...S.btnSm,color:"#7a1c2e"}} onClick={() => exportPDF(w.name)}>{t.exportPDF}</button>
                  </td>
                </tr>
              ))}
            </tbody></table>
            <button style={{...S.btn,marginTop:"12px"}} onClick={() => exportPDF(null)}>{t.exportPDF} ({t.allWorkers})</button>
          </div>

          {/* Edit Sessions */}
          <div style={S.card}>
            <p style={S.sec}>{t.editSessions}</p>
            <select style={S.sel} value={selWorker} onChange={e => {
              const w = e.target.value;
              setSelWorker(w); setSessions([]);
              if (w) api(`getSessions?name=${encodeURIComponent(w)}&month=${reportMonth}`)
                .then(setSessions).catch(() => showToast(t.error));
            }}>
              <option value="">— {t.selectWorker} —</option>
              {workers.map(w => <option key={w} value={w}>{w}</option>)}
            </select>
            {selWorker && (
              <select style={S.sel} value={reportMonth} onChange={e => {
                setReportMonth(e.target.value);
                api(`getSessions?name=${encodeURIComponent(selWorker)}&month=${e.target.value}`)
                  .then(setSessions).catch(() => showToast(t.error));
              }}>
                {allMonths().map(m => <option key={m} value={m}>{monthLabel(m,lang)}</option>)}
              </select>
            )}
            {/* Add session manually */}
            {selWorker && (
              <div style={{marginBottom:"1rem", padding:"10px", background:"var(--color-background-secondary)", borderRadius:"8px"}}>
                <p style={{...S.sec, marginBottom:"8px"}}>{t.addSessionTitle}</p>
                <SessionFields value={newSession} onChange={setNewSession} t={t} />
                <button style={S.btn} onClick={handleAddSession} disabled={busy||!selWorker}>{t.addSession}</button>
              </div>
            )}
            {selWorker && (
              <EditableSessionList
                sessions={monthSessions(reportMonth)} t={t}
                editTarget={editTarget} editData={editData}
                onEditStart={s => { setEditTarget(s); setEditData({date:s.date,start:s.start,end:s.end}); }}
                onEditChange={setEditData}
                onEditSave={saveEdit}
                onEditCancel={() => setEditTarget(null)}
                onDelete={deleteSession}
                busy={busy}
              />
            )}
          </div>
        </>)}
      </div>
      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  );
}